import React, { useState, useEffect, useRef } from "react";
import { http } from "../../axios/axios";
import AdminEditPage from "./AdminEditPage";
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Settings2,
  ShieldCheck,
  CornerDownRight,
} from "lucide-react";

// --- RECURSIVE SUB-MENU COMPONENT ---
const RecursiveItem = ({ item, depth = 0, currentSlug, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSub = item.isDropdown && item.links && item.links.length > 0;
  const isActive = currentSlug === item.path.replace(/^\//, "");

  return (
    <div className="w-full">
      <div
        className={`flex items-center justify-between rounded-xl transition-all ${isActive ? "bg-blue-600/20" : "hover:bg-white/5"}`}
      >
        <button
          onClick={() => onSelect(item.path, item.name)}
          className={`flex-1 text-left px-4 py-3 flex items-center gap-3 ${isActive ? "text-blue-500 font-bold" : "text-slate-400 hover:text-slate-200"}`}
        >
          {depth > 0 && <CornerDownRight size={12} className="opacity-30" />}
          <span className="text-[10px] font-black uppercase tracking-widest">
            {item.name}
          </span>
        </button>

        {hasSub && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className={`p-2 mr-1 rounded-lg transition-colors ${isExpanded ? "text-blue-500 bg-blue-500/10" : "text-slate-600 hover:bg-white/10"}`}
          >
            {isExpanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </button>
        )}
      </div>

      {hasSub && isExpanded && (
        <div className="ml-4 border-l border-white/5 pl-2 mt-1 space-y-1 animate-in slide-in-from-left-2 duration-200">
          {item.links.map((subItem) => (
            <RecursiveItem
              key={subItem._id}
              item={subItem}
              depth={depth + 1}
              currentSlug={currentSlug}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ManageDynamicPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [currentSlug, setCurrentSlug] = useState("");
  const [currentName, setCurrentName] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    http
      .get("/navbar")
      .then((res) => {
        const items = res.data?.data?.menuItems || [];
        setMenuItems(items);
        if (items.length > 0) handleSelect(items[0].path, items[0].name);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSelect = (path, name) => {
    if (!path) return;
    const slug = path === "/" ? "home" : path.replace(/^\//, "");
    setCurrentSlug(slug);
    setCurrentName(name);
    setActiveDropdown(null);
  };

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center bg-[#07090d]">
        <Loader2 className="animate-spin text-blue-500" />
      </div>
    );

  return (
    <div className="min-h-screen text-slate-200 p-8">
      {/* 1. TOP NAV - THE FIX FOR POSITIONING AND Z-INDEX */}
      <div
        className="max-w-7xl mx-auto mb-10 relative"
        style={{ zIndex: 9999 }} // High z-index for the whole nav area
        ref={dropdownRef}
      >
        <div className="bg-[#11141d] border border-white/5 rounded-[2.5rem] p-4 flex items-center gap-4 shadow-2xl relative overflow-visible">
          <div className="flex items-center gap-3 px-6 border-r border-white/5 mr-2 hidden lg:flex">
            <LayoutDashboard size={20} className="text-blue-500" />
            <h3 className="text-[10px] font-bold text-white uppercase tracking-widest">
              Navigator
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {menuItems.map((item, idx) => (
              <div key={item._id} className="relative">
                <button
                  onClick={() =>
                    item.isDropdown
                      ? setActiveDropdown(activeDropdown === idx ? null : idx)
                      : handleSelect(item.path, item.name)
                  }
                  className={`flex items-center gap-3 px-5 py-3 rounded-2xl border transition-all duration-300 ${
                    activeDropdown === idx ||
                    currentSlug === item.path.replace(/^\//, "")
                      ? "bg-[#1a1f2b] border-blue-500 text-white shadow-[0_0_20px_rgba(59,130,246,0.1)]"
                      : "bg-[#0a0c12] border-white/5 text-slate-400 hover:border-white/20"
                  }`}
                >
                  <span className="text-[11px] font-bold uppercase tracking-tight">
                    {item.name}
                  </span>
                  {item.isDropdown && (
                    <ChevronDown
                      size={14}
                      className={activeDropdown === idx ? "rotate-180" : ""}
                    />
                  )}
                </button>

                {/* THE DROPDOWN WINDOW: Absolute positioning prevents item shifting */}
                {activeDropdown === idx && item.isDropdown && (
                  <div className="absolute left-0 mt-4 w-72 bg-[#161b26] border border-white/10 rounded-[2rem] shadow-[0_30px_70px_rgba(0,0,0,0.8)] p-3 animate-in fade-in slide-in-from-top-2 z-[10000]">
                    <div className="max-h-[450px] overflow-y-auto custom-scrollbar">
                      {item.links.map((link) => (
                        <RecursiveItem
                          key={link._id}
                          item={link}
                          currentSlug={currentSlug}
                          onSelect={handleSelect}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2. EDITOR AREA - LOWER Z-INDEX TO STAY BEHIND DROPDOWNS */}
      <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1 }}>
        <div className="flex items-center gap-4 mb-8 px-6">
          <div className="w-1.5 h-6 bg-blue-600 rounded-full shadow-[0_0_10px_#2563eb]" />
          <h2 className="text-xs font-black text-slate-500 uppercase tracking-widest">
            NODE EDITOR: <span className="text-white ml-2">{currentName}</span>
          </h2>
        </div>

        {currentSlug ? (
          <div
            key={currentSlug}
            className="animate-in fade-in slide-in-from-bottom-4 duration-500"
          >
            <AdminEditPage slug={currentSlug} />
          </div>
        ) : (
          <div className="bg-[#11141d] rounded-[3rem] p-32 border border-white/5 text-center">
            <ShieldCheck size={48} className="mx-auto text-slate-800 mb-6" />
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em]">
              Awaiting Selection
            </p>
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #2563eb; }
      `}</style>
    </div>
  );
};

export default ManageDynamicPage;
